import { type PropsWithChildren, useCallback } from 'react';

import type { ThemeContextValue } from './ThemeContext';
import { ThemeContext } from './ThemeContext';

export enum Theme {
  Default = 'default',
  Foundation = 'foundation',
  GreenBean = 'green-bean',
}

const getRootElement = () => document.documentElement;

const getTargetElement = (
  element?: HTMLElement | null
): HTMLElement | undefined => {
  if (typeof document === 'undefined') {
    return undefined;
  }

  if (element === null) {
    throw new Error('The provided theming element does not exist');
  }

  return element ?? getRootElement();
};

export interface ThemeProviderProps extends PropsWithChildren {
  /* The initial value of the theme, as an alternative to calling setTheme directly */
  theme?: Theme;
}

export function ThemeProvider({
  children,
  theme: initialTheme,
}: ThemeProviderProps) {
  const getTheme: ThemeContextValue['getTheme'] = useCallback(element => {
    const target = getTargetElement(element);
    if (!target) {
      return undefined;
    }
    return (target.dataset['tfdTheme'] ??
      getRootElement().dataset['tfdTheme']) as Theme;
  }, []);

  const getCSSVariable: ThemeContextValue['getCSSVariable'] = useCallback(
    (cssVariable, element) => {
      const target = getTargetElement(element);
      if (!target) {
        return undefined;
      }
      return (
        (
          getComputedStyle(target).getPropertyValue(cssVariable) ||
          getComputedStyle(getRootElement()).getPropertyValue(cssVariable)
        ).trim() || undefined
      );
    },
    []
  );

  const setTheme: ThemeContextValue['setTheme'] = useCallback(
    (theme, element) => {
      const target = getTargetElement(element);
      if (!target) {
        return;
      }
      if (!theme) {
        delete target.dataset.tfdTheme;
      } else {
        target.dataset['tfdTheme'] = theme;
      }
    },
    []
  );

  const setCSSVariable: ThemeContextValue['setCSSVariable'] = useCallback(
    (cssVariable, cssValue, element) => {
      const target = getTargetElement(element);
      if (!target) {
        return;
      }
      if (cssValue) {
        target.style.setProperty(cssVariable, cssValue);
      } else {
        target.style.removeProperty(cssVariable);
      }
    },
    []
  );

  if (initialTheme) {
    setTheme(initialTheme);
  }

  return (
    <ThemeContext.Provider
      value={{
        getTheme,
        getCSSVariable,
        setTheme,
        setCSSVariable,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}
